import { SelfDescription } from "../../quiz/describeSelf";
import { Experience } from "../../quiz/experience";
import { OtherPrograms } from "../../quiz/otherPrograms";
import { ScienceFamiliarity } from "../../quiz/scienceFamiliarity";
import { WhyConsistency } from "../../quiz/whyConsistency";
import i18n, { QuizLanguage } from "../../i18n";

export const MATCH_OFFER_STORAGE_KEY = "bws_web46_match_offer";

export type MatchReasonId = "1" | "2" | "3" | "4" | "5" | "6" | "7a" | "7b";

export interface MatchReason {
  id: MatchReasonId;
  headline: string;
  line1: string;
  line2?: string;
  consequence: string;
}

export interface MatchOfferAnswers {
  followingus?: unknown;
  otherPrograms?: unknown;
  science?: unknown;
  duration?: unknown;
  describeSelf?: unknown;
  whyConsistency?: unknown;
  experience?: unknown;
}

export interface MatchOfferState {
  shownAt: number;
  claimedAt?: number;
  dismissedAt?: number;
  reason: MatchReason;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

// Struggle lines from the PRD table. "Good habits but inconsistent" without a
// consistency answer falls back to the time line, as in the PRD artifact.
const struggleLine = (answers: MatchOfferAnswers, t: Translate): string => {
  if (answers.describeSelf === SelfDescription.DietAndTrainingNeedWork) {
    return t("struggle.fromScratch");
  }
  if (answers.describeSelf === SelfDescription.SlowProgress) {
    return t("struggle.slowProgress");
  }
  switch (answers.whyConsistency) {
    case WhyConsistency.Motivation:
      return t("struggle.motivation");
    case WhyConsistency.Injuries:
      return t("struggle.injuries");
    case WhyConsistency.ProgramHopping:
      return t("struggle.programHopping");
    default:
      return t("struggle.time");
  }
};

const withName = (name: string | undefined, headline: string): string => {
  const trimmedName = name?.trim();
  if (trimmedName) return `${trimmedName}, ${headline}`;
  return headline.charAt(0).toUpperCase() + headline.slice(1);
};

const joinTriedItems = (items: string[], t: Translate): string => items.slice(0, 2).join(t("and"));

// Paid attempts, in the PRD artifact's order (it lists the first two).
// The second element is the copy key under results.matchOffer.tried.
const PAID_ATTEMPTS: Array<[OtherPrograms, string]> = [
  [OtherPrograms.PersonalTrainer, "personalTrainer"],
  [OtherPrograms.PaidMealPlans, "paidMealPlans"],
  [OtherPrograms.OnlineFitnessProgram, "onlineFitnessProgram"],
];

// Do-it-yourself attempts, in the PRD artifact's order.
const DIY_ATTEMPTS: Array<[OtherPrograms, string]> = [
  [OtherPrograms.CalorieCounting, "calorieCounting"],
  [OtherPrograms.Keto, "keto"],
  [OtherPrograms.IntermittentFasting, "intermittentFasting"],
  [OtherPrograms.SelfGuidedFitnessProgram, "selfGuidedProgram"],
];

// Stored `duration` answer -> copy key under results.matchOffer.duration.
const DURATION_KEYS: Record<string, string> = {
  "0-6 months": "upTo6Months",
  "6-12 months": "sixTo12Months",
  "1-3 years": "oneTo3Years",
  "3+ years": "threePlusYears",
};

// Stored `followingus` answer -> copy key under results.matchOffer.following.
const FOLLOWING_KEYS: Record<string, string> = {
  "1 to 2 years": "oneTo2Years",
  "2+ years": "twoPlusYears",
};

const CONSEQUENCE_KEYS: Record<MatchReasonId, string> = {
  "1": "consequence.r1",
  "2": "consequence.r2",
  "3": "consequence.r3",
  "4": "consequence.r4",
  "5": "consequence.r5",
  "6": "consequence.r6",
  "7a": "consequence.r7a",
  "7b": "consequence.r7b",
};

/**
 * Chooses the first matching reason, 1 → 7b. This mirrors `pickReason` in the
 * PRD artifact (QuizMatchOfferPRD.jsx) on our stored answer values; the
 * parity check in .polaris/fable-advisor/reason-parity.cjs compares the two on
 * every answer combination. Missing `describeSelf` is deliberately ineligible
 * for the match modal (PRD trigger rules).
 *
 * The copy is in `lng` (English by default, which is what gets stored); the
 * reason id, the only value sent to Amplitude, does not depend on it.
 */
export const pickMatchReason = (
  answers: MatchOfferAnswers,
  name?: string,
  lng: QuizLanguage = "en",
): MatchReason | null => {
  if (!Object.values(SelfDescription).includes(answers.describeSelf as SelfDescription)) {
    return null;
  }
  const t: Translate = (key, options) => i18n.t(`results.matchOffer.${key}`, { ...options, lng });

  const attempted = Array.isArray(answers.otherPrograms) ? answers.otherPrograms : [];
  const paid = PAID_ATTEMPTS.filter(([value]) => attempted.includes(value));
  const diy = DIY_ATTEMPTS.filter(([value]) => attempted.includes(value));
  const isSkeptic =
    answers.science === ScienceFamiliarity.Attempted ||
    answers.science === ScienceFamiliarity.NotAtAll;
  const duration = typeof answers.duration === "string" ? answers.duration : "";
  const isLongTime = duration === "1-3 years" || duration === "3+ years";
  const durationText = DURATION_KEYS[duration] ? t(`duration.${DURATION_KEYS[duration]}`) : undefined;

  let id: MatchReasonId;
  if (answers.followingus === "1 to 2 years" || answers.followingus === "2+ years") id = "1";
  else if (paid.length > 0) id = "2";
  else if (diy.length > 0 && isSkeptic) id = "3";
  else if (answers.describeSelf === SelfDescription.SlowProgress && isLongTime) id = "4";
  else if (answers.science === ScienceFamiliarity.Somewhat) id = "5";
  else if (
    answers.describeSelf === SelfDescription.DietAndTrainingNeedWork ||
    answers.experience === Experience.Beginner ||
    duration === "0-6 months"
  ) id = "6";
  else if (answers.describeSelf === SelfDescription.StruggleConsistency) id = "7a";
  else id = "7b";

  let headline: string;
  let line1: string;
  let line2 = struggleLine(answers, t);
  let consequence = t(CONSEQUENCE_KEYS[id]);

  switch (id) {
    case "1": {
      const followingKey = FOLLOWING_KEYS[answers.followingus as string];
      headline = withName(name, t("headline.thankYou"));
      line1 = t("line.followedJeremy", {
        duration: followingKey ? t(`following.${followingKey}`) : String(answers.followingus),
      });
      break;
    }
    case "2": {
      const isOnlineOnly = paid.length === 1 && paid[0][0] === OtherPrograms.OnlineFitnessProgram;
      headline = withName(name, t(isOnlineOnly ? "headline.triedPlans" : "headline.invested"));
      line1 = t("line.tried", { items: joinTriedItems(paid.map(([, key]) => t(`tried.${key}`)), t) });
      if (isOnlineOnly) consequence = t("consequence.onlineOnly");
      break;
    }
    case "3":
      headline = withName(name, t("headline.triedPlans"));
      line1 = t("line.tried", { items: joinTriedItems(diy.map(([, key]) => t(`tried.${key}`)), t) });
      line2 = answers.science === ScienceFamiliarity.Attempted
        ? t("line.readAdvice")
        : t("line.readyForScience");
      break;
    case "4":
      headline = withName(name, t("headline.effortDeserves", { duration: durationText }));
      line1 = t("line.workingFor", { duration: durationText });
      break;
    case "5":
      headline = withName(name, t("headline.knowPlenty"));
      line1 = t("line.knowFairBit");
      break;
    case "6":
      headline = withName(name, t("headline.startingHardest"));
      line1 = answers.describeSelf === SelfDescription.DietAndTrainingNeedWork
        ? t("struggle.fromScratch")
        : answers.experience === Experience.Beginner
          ? t("line.newToGym")
          : t("line.startedRecently");
      break;
    case "7a":
      headline = withName(name, t("headline.haveHabits"));
      line1 = t("line.goodHabits");
      break;
    default:
      headline = withName(name, t("headline.puttingInWork"));
      line1 = t("struggle.slowProgress");
      line2 = durationText ? t("line.atItFor", { duration: durationText }) : "";
  }

  return {
    id,
    headline,
    line1,
    ...(line2 && line2 !== line1 ? { line2 } : {}),
    consequence,
  };
};

const isMatchReason = (value: unknown): value is MatchReason => {
  if (!value || typeof value !== "object") return false;
  const reason = value as Partial<MatchReason>;
  return (
    ["1", "2", "3", "4", "5", "6", "7a", "7b"].includes(reason.id || "") &&
    typeof reason.headline === "string" &&
    typeof reason.line1 === "string" &&
    (reason.line2 === undefined || typeof reason.line2 === "string") &&
    typeof reason.consequence === "string"
  );
};

export const readMatchOfferState = (): MatchOfferState | null => {
  try {
    const rawState = localStorage.getItem(MATCH_OFFER_STORAGE_KEY);
    if (!rawState) return null;
    const state = JSON.parse(rawState) as Partial<MatchOfferState>;
    if (
      typeof state.shownAt !== "number" ||
      (state.claimedAt !== undefined && typeof state.claimedAt !== "number") ||
      (state.dismissedAt !== undefined && typeof state.dismissedAt !== "number") ||
      !isMatchReason(state.reason)
    ) {
      return null;
    }
    return state as MatchOfferState;
  } catch {
    return null;
  }
};

export const writeMatchOfferState = (state: MatchOfferState): void => {
  try {
    localStorage.setItem(MATCH_OFFER_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // The offer still works for this page view when storage is unavailable.
  }
};
